import { useNavigate } from "@vixi/router";
import { AltLoginButton } from "./login";
import { useForm } from "react-hook-form";
import { useSignup } from "@/hooks/api/auth/useSignup";
import { LangKey, useTranslation } from "@vixi/localization";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { RouteOptionsById, RouterIds } from "@vixi/router";
import { Img, TextInput, Button, FieldLabel, toast, LoadingSpinner, TextLink } from "@vixi/ui";
import { useLogin } from "@vixi/api";

// Constants
const routeId = "/_auth/signup" satisfies RouterIds;
export const SignUpPageOptions: RouteOptionsById<typeof routeId> = {
  component: RouteComponent,
};

const signupValidationSchema = z.object({
  email: z.string().email("ErrorMessage.InvalidEmail" satisfies LangKey),
  password: z.string().min(8),
  firstName: z.string().min(2),
  lastName: z.string().min(2),
  phoneNumber: z.string().optional(),
});

type SignUpFormValues = z.infer<typeof signupValidationSchema>;

function RouteComponent() {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpFormValues>({
    defaultValues: {
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      phoneNumber: "",
    },
    resolver: zodResolver(signupValidationSchema),
  });
  const navigate = useNavigate();

  const { mutateAsync: signUp, isError, isPending, error } = useSignup();
  const { mutateAsync: login, isPending: isLoginPending } = useLogin();

  const onSubmit = async (data: SignUpFormValues) => {
    await signUp(data);
    await login({
      email: data.email,
      password: data.password,
    });
    navigate({ to: "/create" });
  };

  const isLoading = isPending || isLoginPending;

  return (
    <div className="my-auto flex flex-col">
      <div className="mt-7.5 mb-10 flex flex-col gap-y-6">
        <h1 className="font-semibold">{t("Auth.SignUp")}</h1>

        <form className="flex flex-col gap-y-5" onSubmit={handleSubmit(onSubmit)}>
          {!isLoading ? (
            <>
              <div>
                <FieldLabel size="medium">{t("Account.Email")}</FieldLabel>
                <TextInput
                  type="email"
                  placeholder="Ex: mybook@domain.com"
                  autoComplete="email"
                  {...register("email")}
                />
                {errors.email && (
                  <p className="text-destructive text-sm">{t(errors.email.message as LangKey)}</p>
                )}
              </div>
              <div>
                <FieldLabel size="medium">{t("Account.FirstName")}</FieldLabel>
                <TextInput
                  placeholder={t("Account.FirstName")}
                  autoComplete="name given-name"
                  {...register("firstName")}
                />
                {errors.firstName && (
                  <p className="text-destructive text-sm">{errors.firstName.message}</p>
                )}
              </div>
              <div>
                <FieldLabel size="medium">{t("Account.LastName")}</FieldLabel>
                <TextInput
                  placeholder={t("Account.LastName")}
                  autoComplete="name family-name"
                  {...register("lastName")}
                />
                {errors.lastName && (
                  <p className="text-destructive text-sm">{errors.lastName.message}</p>
                )}
              </div>
              <div>
                <FieldLabel size="medium">{t("Account.Password")}</FieldLabel>
                <TextInput
                  type="password"
                  placeholder={t("Account.Password")}
                  autoComplete="new-password"
                  {...register("password")}
                />
                {errors.password && (
                  <p className="text-destructive text-sm">{errors.password.message}</p>
                )}
              </div>
            </>
          ) : (
            <div className="mx-auto my-12.5">
              <LoadingSpinner />
            </div>
          )}

          {isError && (
            <div className="my-3 space-y-1.5">
              <div className="w-82.5">
                {Object.entries(error.response?.data.errors ?? {}).map(([key, _]) => {
                  const langKey = `BackendMessage.${key}` as LangKey;
                  return <p className="text-destructive text-sm">{t(langKey)}</p>;
                })}
              </div>
            </div>
          )}
          <div className="mt-0.5 text-center">
            <Button type="submit" variant="contained" size="medium" disabled={isLoading}>
              {t("Auth.SignUp")}
            </Button>
          </div>
        </form>

        <h4 className="fs-18">
          {t("Auth.AlreadyHaveAnAccount")}{" "}
          <TextLink
            disabled={isPending}
            to="/login"
            underline="none"
            className="fs-18 text-primary"
          >
            {t("Auth.LogInNow")}
          </TextLink>
        </h4>
      </div>

     
    </div>
  );
}
